import wrapperHOC from "@Utils/wrapperHOC";
import { memo, useEffect, useState } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import useBlogBasePath from "@Pages/Blog/utils/useBlogBasePath";
import type { AppState } from "@Store/index";
import { renderLexicalContent, generateTableOfContents } from "./helper";
import { renderToStaticMarkup } from "react-dom/server";
import HeaderDesktop from "@Components/Header/HeaderDesktop";
import FooterDesktop from "@Components/FooterV2/FooterDesktop";
import Header from "@Components/Header";
import FooterV2 from "@Components/FooterV2/FooterV2";
import useIsDesktop from "hooks/useIsDesktop";
import HundredsOfCities from "@Pages/Home/components/HundredsOfCities";
import NotFound from "@Pages/NotFound";
import BlogSkeleton from "./components/BlogSkeleton";
import BlogCTABanner from "./components/BlogCTABanner/BlogCTABanner";
import BlogDownloadBanner from "./components/BlogDownloadBanner/BlogDownloadBanner";
import BlogVideoEmbed from "./components/BlogVideoEmbed/BlogVideoEmbed";
import BlogPostContent from "./BlogPostContent";
import DraftBanner from "./components/DraftBanner";
import Image from "@Components/Image";
import AppPromotionBanner from "@Components/AppPromotionBanner";
import { PAGE } from "constants/configConstants";
import { updateShowAppBanner } from "@App/appReducer";
import FeaturedBlogSection from "@Pages/Blog/BlogHome/components/FeaturedBlogSection/FeaturedBlogSection";

const getFeaturedBlogTitle = (blogData: any): string => {
  const city = blogData?.city;
  const cityName = city && typeof city === "object" ? city.name : null;
  if (cityName && cityName.toLowerCase() !== "multi-city") {
    return `More Blogs on ${cityName}`;
  }
  const country = blogData?.country || blogData?.countryTag;
  const countryName = country && typeof country === "object" ? country.name : null;
  if (countryName) {
    return `More Blogs on ${countryName}`;
  }
  return "Latest Blogs";
};

const BlogPost = () => {
  const {
    data: blogData,
    readyStatus,
    sidebarFeaturedBlogs,
  } = useSelector((state: AppState) => state.blogPageData);
  const isDesktop = useIsDesktop();
  const [isDraft, setIsDraft] = useState(false);
  const dispatch = useDispatch();
  const basePath = useBlogBasePath();

  const { showAppBanner } = useSelector(
    ({ appData }: AppState) => ({
      showAppBanner: appData.showAppBanner,
    }),
    shallowEqual,
  );

  const handleCloseBanner = () => {
    dispatch(updateShowAppBanner(false));
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    // Check if the current path is a draft blog post
    if (typeof window !== "undefined") {
      const isDraftPage = window.location.pathname?.startsWith("/drafts-blog/");
      setIsDraft(isDraftPage);
    }
  }, []);

  const isLoading = readyStatus === "request" || readyStatus === "invalid";
  const hasError = readyStatus === "failure";

  // Format date helper
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  };

  // Render article content with component registry
  const articleContent = blogData?.postBody
    ? renderLexicalContent(blogData.postBody, {
        BlogCTABanner,
        BlogDownloadBanner,
        BlogVideoEmbed,
        Image,
      })
    : [];

  // Generate dynamic table of contents based on tocBasedOn field
  const tableOfContents = blogData?.tocBasedOn
    ? generateTableOfContents(blogData.postBody, blogData.tocBasedOn)
    : [];

  // Transform FAQ items with rich text answers to HTML
  const processedFaqItems = blogData?.faqItems
    ? blogData.faqItems.map((faqItem: any) => {
        // If answerRichText exists, convert it to HTML string
        if (faqItem.answerRichText) {
          const richTextElements = renderLexicalContent(faqItem.answerRichText);
          const htmlString = richTextElements
            .filter(
              (element): element is React.ReactElement => element !== null && element !== undefined,
            )
            .map((element) => renderToStaticMarkup(element))
            .join("");

          return {
            ...faqItem,
            answer: htmlString,
            question: faqItem.question,
          };
        }

        // Fallback to plain answer if it exists
        return {
          ...faqItem,
          answer: faqItem.answer || "",
          question: faqItem.question,
        };
      })
    : [];

  if (isLoading) {
    return (
      <>
        {isDesktop ? (
          <HeaderDesktop isBlogHeader parentPage={PAGE.BLOG} />
        ) : (
          <>
            {showAppBanner && (
              <AppPromotionBanner from={PAGE.BLOG} onClose={handleCloseBanner} addSticky />
            )}
            <Header isBlogHeader />
          </>
        )}
        <BlogSkeleton isDesktop={isDesktop} />
        {isDesktop ? <FooterDesktop /> : <FooterV2 />}
      </>
    );
  }

  if (hasError || !blogData) {
    return <NotFound />;
  }

  return (
    <>
      {isDraft && <DraftBanner />}
      {isDesktop ? (
        <HeaderDesktop isBlogHeader parentPage={PAGE.BLOG} />
      ) : (
        <>
          {showAppBanner && (
            <AppPromotionBanner from={PAGE.BLOG} onClose={handleCloseBanner} addSticky />
          )}
          <Header isBlogHeader />
        </>
      )}
      <BlogPostContent
        blogData={blogData}
        articleContent={articleContent}
        tableOfContents={tableOfContents}
        processedFaqItems={processedFaqItems}
        formatDate={formatDate}
        basePath={basePath}
      />
      <FeaturedBlogSection
        blogs={sidebarFeaturedBlogs}
        basePath={basePath}
        currentBlogSlug={blogData?.slug}
        title={getFeaturedBlogTitle(blogData)}
      />
      <HundredsOfCities isDesktop={isDesktop} />
      {isDesktop ? <FooterDesktop /> : <FooterV2 />}
    </>
  );
};

export default wrapperHOC(memo(BlogPost), {
  componentName: "BlogPost-Blog",
  showForChina: true,
});
